/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertTriangle, ShieldCheck, Activity, Loader2 } from 'lucide-react';
import { scoreNoShow } from '@/lib/ai_clinical';
import { cn } from '@/lib/utils';

const riskCls = {
  low:    'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  medium: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
  high:   'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30',
};

/**
 * NoShowRiskBadge — small badge that lazily scores an appointment.
 * Click to fetch or refresh. Shows tooltip with reasons.
 */
export const NoShowRiskBadge = ({ appointmentId, initialScore }) => {
  const [data, setData] = useState(initialScore || null);
  const [loading, setLoading] = useState(false);

  const fetchScore = async (e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (loading) return;
    setLoading(true);
    try {
      const res = await scoreNoShow(appointmentId);
      setData(res.result);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  if (!data) {
    return (
      <button
        type="button"
        onClick={fetchScore}
        className="inline-flex items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 text-[10px] text-muted-foreground hover:bg-accent/40 transition-colors"
        data-testid="noshow-fetch-btn"
      >
        {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Activity className="h-3 w-3" />}
        Rischio no-show
      </button>
    );
  }

  const risk = data.risk_level || 'low';
  const Icon = risk === 'high' ? AlertTriangle : risk === 'medium' ? Activity : ShieldCheck;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" onClick={fetchScore} className="inline-flex" data-testid="noshow-badge">
            <Badge variant="outline" className={cn('gap-1 cursor-pointer', riskCls[risk])}>
              <Icon className="h-3 w-3" /> No-show: {data.score ?? 0}/100
            </Badge>
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-1 text-xs">
            <div className="font-semibold">Rischio {risk}</div>
            {data.explanation && <div>{data.explanation}</div>}
            {data.top_reasons?.length > 0 && (
              <ul className="list-disc pl-4 mt-1">
                {data.top_reasons.map((r) => <li key={r}>{r}</li>)}
              </ul>
            )}
            {data.recommended_interventions?.length > 0 && (
              <div className="mt-1">
                <div className="font-semibold">Azioni suggerite:</div>
                <ul className="list-disc pl-4">
                  {data.recommended_interventions.map((i) => <li key={i.action}>{i.action}</li>)}
                </ul>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
